import { Button, Modal } from "@ts-digital/vrc";
import numeral from "numeral";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import ReconciliationForm from "../deadlines/details/register/reconciliation";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { splitDeadlines } from "../../store/actions/deadlines/detail";
import { orderBySingleField } from "../../libs/arrayUtils";
import { intersectionWith } from "../../libs/arrayUtils";
import { useEffect } from "react";
import { getReconciledSummary } from "../../store/actions/reconciliation/operations";
import {delay} from "../../libs/utils";

const ModalDeadlinesBigger = props => {
  const {
    difference,
    isModalVisible,
    setIsModalVisible,
    cancelText,
    saveText,
    selectedMovementDetails,
    selectedDeadlinesAmount,
    paymentValueResidue,
    setPaymentValueResidue,
    checkbox,
    setCheckbox,
    dateValueResidue,
    setDateValueResidue,
    valueRadio,
    setValueRadio,
    openDeadlineDetail,
    selectedDeadlines,
    invalidSelectionBiggerDeadlines,
    setInvalidSelectionBiggerDeadlines,
    reconcilableDeadlines,
    deadlinesToReconcile,
    createReconciliationLink,
    setBlockMask,
    setDisableReconciliationButton,
    getReconcilableDeadlines,
    setReconcilableDeadlines,
    selectedMovements,
    startDate,
    endDate,
    makeCardAnimation
  } = props;

  const main = useSelector(state => state.main);
  const auth = useSelector(state => state.auth);
  const isSplittingDeadlines = useSelector(state => state.deadlinesDetail.splittedDeadlines.isSplitting);

  const [deadlineToSplit, setDeadlineToSplit] = useState(null);

  const [defaultValueToSplit, setDefaultValueToSplit] = useState(null);

  useEffect(() => {
    const sortedDeadlinesToReconcile = orderBySingleField(deadlinesToReconcile,
      "date");
    setDeadlineToSplit(sortedDeadlinesToReconcile[0]);
  }, [deadlinesToReconcile]);

  useEffect(() => {
    setDefaultValueToSplit(deadlineToSplit);
  }, [deadlineToSplit]);

  const amountResidueText = useIntl().formatMessage({
    id: "deadlines.merge.residual"
  });
  const paymentValue = selectedMovementDetails.paymentValue;
  const movementDate = selectedMovementDetails.date;
  const movementDescription = selectedMovementDetails.description;
  const movementAmount = selectedMovementDetails.amount;

  const dispatch = useDispatch();

  const createReconciliation = res => {
    let linkList = [];
    // recupero l'id della prima nuova scadenza creata dall'azione di split, ovvero la scadenza originaria meno la differenza, quella che vogliamo riconciliare
    const deadlineToReconcileAfterSplitId = res?.created?.[0]?.id;

    // filtro dalle scadenze selezionate quella originaria che verrà frazionata
    const deadlinesId = selectedDeadlines.filter(d => d !== deadlineToSplit?.id);

    // aggiungo l'id sopra ricavato alla lista(filtrata) di id da accoppiare al movimento bancario per la riconciliazione
    const deadlineIdsList = [...deadlinesId, deadlineToReconcileAfterSplitId];
    const bankTransactionId = selectedMovements[0];

    if (selectedDeadlines.length > 1) {
      deadlineIdsList.forEach(deadlineId =>
        linkList.push({ deadlineId, bankTransactionId }));
    } else {
      linkList = [
        {
          deadlineId: deadlineToReconcileAfterSplitId,
          bankTransactionId: bankTransactionId
        }
      ];
    }

    dispatch(createReconciliationLink(
      auth,
      main.accountantUuidSelected,
      main.companyUuidSelected,
      linkList,
      "BANK_TRANSACTION"
    ))
      .then(res => {
        delay(3000).then(() => dispatch(getReconciledSummary()));
        setBlockMask();
        setDisableReconciliationButton(true);
        makeCardAnimation();
        dispatch(getReconcilableDeadlines(
          selectedMovements,
          startDate,
          endDate
        ));
      })
      .then(response => {
        setReconcilableDeadlines(response?.data.filteredDeadlines);
      });
  };

  // funzione per calcolare e aggiungere alla lista le scadenze che restano fuori dal processo di split
  // in caso di selezione di più di due scadenze

  const calculateAndAddDeadlines = (deadlines, existingList) => {
    // salvo in un array le scadenze selezionate che non devono essere splittate
    const remainingDeadlines = intersectionWith(
      deadlines, existingList, (a, b) => {
        return a.date === b.date && a.amount === b.amount && a.paymentModeId === b.paymentModeId && a.paymentStatus === b.paymentStatus && a.execution?.mode === b.execution?.mode && a.execution?.date === b.execution?.date;
      }
    );
    // aggiungo alla nuova lista ogni altra scadenza selezionata per la riconciliazione
    if (remainingDeadlines.length > 0) {
      remainingDeadlines.forEach(deadline => {
        existingList.push({
          date: moment(deadline?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
          amount: deadline?.totalAmount,
          paymentModeId: deadline?.payment.mode.id,
          execution: {
            mode: deadline?.payment.mode.id,
            date: moment(deadline?.date).format("YYYY-MM-DD[T]HH:mm:ss")
          },
          paymentStatus: "paid"
        });
      });
    }
  };

  const splitBiggerDeadlines = () => {
    // decido qual è la scadenza da splittare: nel caso sia una selezione multipla,
    // sarà la scadenza flaggata all'interno della modale (deadline to split), altrimenti sarà l'unica scadenza selezionata

    const splittingDeadline =
      deadlinesToReconcile.length > 1
        ? deadlineToSplit
        : deadlinesToReconcile?.[0];

    // creo la nuova lista con la scadenza da splittare e quella data dal residuo
    let newList = [
      {
        date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          splittingDeadline?.payment.type === "item_pays"
            ? splittingDeadline?.totalAmount - difference
            : splittingDeadline?.totalAmount - Math.abs(difference),
        paymentModeId: splittingDeadline?.payment?.mode?.id,
        execution: {
          mode: splittingDeadline?.payment?.mode?.id,
          date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss")
        },
        paymentStatus: "paid"
      },
      {
        date: moment(dateValueResidue).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          splittingDeadline?.payment.type === "item_pays"
            ? -Math.abs(difference)
            : Math.abs(difference),
        paymentModeId: paymentValueResidue,
        paymentStatus: checkbox ? "suspended" : "due"
      }
    ];

    calculateAndAddDeadlines(deadlinesToReconcile, newList);

    // eseguo il dispatch dell'azione di split e al termine eseguo la riconciliazione vera e propria chiamando il metodo createReconciliation()

    dispatch(splitDeadlines(
      splittingDeadline?.id,
      newList,
      true
    )).then(res => {
      setIsModalVisible(false);
      createReconciliation(res);
    });
  };

  // funzione per l'abbuona

  const rebateDeadlinesBigger = () => {
    const splittingDeadline = deadlinesToReconcile?.[0];

    // creo la nuova lista da passare alla funzione di split, con la scadenza originaria e il residuo come abbuonato (rebated)

    const newListRebate = [
      {
        date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          splittingDeadline?.payment.type === "item_pays"
            ? splittingDeadline?.totalAmount - difference
            : splittingDeadline?.totalAmount - Math.abs(difference),
        paymentModeId: splittingDeadline?.payment?.mode?.id,
        execution: {
          mode: splittingDeadline?.payment?.mode?.id,
          date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss")
        },
        paymentStatus: "paid"
      },
      {
        date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          splittingDeadline?.payment.type === "item_pays"
            ? -Math.abs(difference)
            : Math.abs(difference),
        paymentModeId: splittingDeadline?.payment?.mode?.id,
        execution: {
          mode: splittingDeadline?.payment?.mode?.id,
          date: moment(splittingDeadline?.date).format("YYYY-MM-DD[T]HH:mm:ss")
        },
        rebated: true,
        paymentStatus: "paid"
      }
    ];

    // eseguo il dispatch dell'azione di split e al termine eseguo la riconciliazione vera e propria chiamando il metodo createReconciliation()

    dispatch(splitDeadlines(
      splittingDeadline?.id,
      newListRebate,
      true
    )).then(res => {
      setIsModalVisible(false);
      createReconciliation(res);
    });
  };

  // funzione eseguita nel caso di più scadenze selezionate: la differenza del totale, da abbuonare, verrà splittata, di default,
  // dalla deadlineToSplit, ovvero, la più recente (la più vicina alla data attuale)
  const rebateMultipleDeadlinesBigger = () => {
    // creo la nuova lista da passare alla funzione di split, inizialmente con la scadenza da splittare e il residuo da abbuonare
    let newListRebate = [
      {
        date: moment(deadlineToSplit?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          deadlineToSplit?.payment.type === "item_pays"
            ? deadlineToSplit?.totalAmount - difference
            : deadlineToSplit?.totalAmount - Math.abs(difference),
        paymentModeId: deadlineToSplit?.payment.mode.id,
        execution: {
          mode: deadlineToSplit?.payment.mode.id,
          date: moment(deadlineToSplit?.date).format("YYYY-MM-DD[T]HH:mm:ss")
        },
        paymentStatus: "paid"
      },
      {
        date: moment(deadlineToSplit?.date).format("YYYY-MM-DD[T]HH:mm:ss"),
        amount:
          deadlineToSplit?.payment.type === "item_pays"
            ? -Math.abs(difference)
            : Math.abs(difference),
        paymentModeId: deadlineToSplit?.payment.mode.id,
        execution: {
          mode: deadlineToSplit?.payment.mode.id,
          date: moment(deadlineToSplit?.date).format("YYYY-MM-DD[T]HH:mm:ss")
        },
        rebated: true,
        paymentStatus: "paid"
      }
    ];

    calculateAndAddDeadlines(deadlinesToReconcile, newListRebate);

    // eseguo il dispatch dell'azione di split e al termine eseguo la riconciliazione vera e propria chiamando il metodo createReconciliation()

    dispatch(splitDeadlines(
      deadlineToSplit?.id,
      newListRebate,
      true
    )).then(res => {
      setIsModalVisible(false);
      createReconciliation(res);
    });
  };

  const manageSaveClick = () => {
    switch (valueRadio) {
    case "1":
      splitBiggerDeadlines();
      break;
    case "2":
      if (deadlinesToReconcile.length === 1){
        rebateDeadlinesBigger();
      } else if (deadlinesToReconcile.length > 1){
        rebateMultipleDeadlinesBigger();
      }
      break;
    default:
      break;
    }

  };
  return (
    <div>
      <Modal
        className={"dbx-register-modal-deadlines-bigger"}
        title={`${amountResidueText}: ${numeral(Math.abs(difference)).format("0,0.00")} €`}
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
        }}
        outsideClosable={false}
        footer={
          <div className="dbx-register-modal-footer">
            <Button
              kind="secondary"
              colorScheme={"ghost"}
              style={{ marginRight: "16px" }}
              className="buttonNav"
              onClick={() => {
                setIsModalVisible(false);
              }}
            >
              {cancelText}
            </Button>
            <Button
              kind="primary"
              style={{ marginRight: "16px" }}
              className="buttonNav"
              disabled={
                valueRadio === "1" &&
                deadlinesToReconcile.length > 1 &&
                deadlineToSplit === null
              }
              onClick={() => manageSaveClick()

              }
            >
              {saveText}
            </Button>
          </div>
        }
      >
        {" "}
        <ReconciliationForm
          amountToPay={difference}
          openDeadlineDetail={openDeadlineDetail}
          residueAmount={difference}
          paymentValue={paymentValue}
          selectedMovementDetails={selectedMovementDetails}
          reconcilableDeadlines={reconcilableDeadlines}
          totalAmount={selectedDeadlinesAmount}
          deadlineDate={movementDate}
          deadlineDescription={movementDescription}
          setPaymentValueResidue={setPaymentValueResidue}
          paymentValueResidue={paymentValueResidue}
          setCheckbox={setCheckbox}
          checkbox={checkbox}
          setDateValueResidue={setDateValueResidue}
          dateValueResidue={dateValueResidue}
          setValueRadio={setValueRadio}
          valueRadio={valueRadio}
          isMultipleReconciliation={true}
          movementAmount={movementAmount}
          selectedDeadlines={selectedDeadlines}
          invalidSelectionBiggerDeadlines={invalidSelectionBiggerDeadlines}
          difference={difference}
          setInvalidSelectionBiggerDeadlines={
            setInvalidSelectionBiggerDeadlines
          }
          isSplittingDeadlines={isSplittingDeadlines}
          deadlinesToReconcile={deadlinesToReconcile}
          setDeadlineToSplit={setDeadlineToSplit}
          defaultValueToSplit={defaultValueToSplit}
          setDefaultValueToSplit={setDefaultValueToSplit}
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "30px",
            maxWidth: "1026px",
            padding: "22px 0px"
          }}
        />
      </Modal>
    </div>
  );
};

export default ModalDeadlinesBigger;
